import React from "react";
import { DefaultFallback, FallbackProps } from "./DefaultFallbackUI";

export type ErrorBoundaryProps = {
  children: React.ReactNode;
  reportError?: (error: Error, info: { componentStack: string }) => void;
  onReset?: () => void;
  FallbackComponent?: React.ComponentType<FallbackProps>;
  allowHardReload?: boolean;
};

export type ErrorBoundaryState = {
  error: Error | null;
  info: { componentStack: string } | null;
};

 class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null, info: null };

  private mounted = false;

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const { reportError } = this.props;
    const payload = { componentStack: info.componentStack };
    this.setState({ info: payload });

    try {
      reportError?.(error, payload);
    } catch {
        console.error("ErrorBoundary: Failed to report error", error, payload);
    }
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;
  }

  reset = () => {
    if (!this.mounted) return;
    this.setState({ error: null, info: null });
    this.props.onReset?.();
  };

  hardReload = () => {
    try {
      if (typeof window !== "undefined") {
        window.location.reload();
      }
    } catch {
        console.error("ErrorBoundary: Failed to hard reload the page, handleReload fun");
    }
  };

  render() {
    const { children, FallbackComponent, allowHardReload } = this.props;

    if (this.state.error) {
      const online = typeof navigator !== "undefined" ? navigator.onLine : true;
      const Fallback = FallbackComponent ?? DefaultFallback;
      return (
        <Fallback
          error={this.state.error}
          info={this.state.info}
          online={online}
          onRetry={this.reset}
          onReload={allowHardReload ? this.hardReload : undefined}
        />
      );
    }

    return <>{children}</>;
  }
}
export default ErrorBoundary;